/**
 * F14.3 — EL TÉCNICO REGISTRA SU TRABAJO: UNA SOLA ENTRADA, EN LÍNEA Y SIN CONEXIÓN
 *
 * La pantalla de campo llama siempre aquí; esta capa decide por dónde va la operación (mismo patrón que
 * `ejecucion-propia.ts` en Limpieza):
 *
 *   1. Si el parte ya tiene operaciones del dispositivo sin llegar al servidor, la nueva se ENCOLA detrás (mismo
 *      orden, misma cadena `dependeDe`): nunca adelanta en línea a una anterior que sigue pendiente.
 *   2. Con conexión, va directa por `POST /api/sync` con UNA operación — el MISMO contrato y la MISMA regla de dominio
 *      que al sincronizar (`backend/src/mantenimiento/campo.ts`), con un `idOperacionCliente` nuevo. Si el dispositivo
 *      está preparado, la operación aplicada se anota en la cola ya SINCRONIZADA (con sus ids reales): lo siguiente
 *      que se haga sin red parte de ella.
 *   3. Si ese envío se queda sin respuesta (o el servidor pide reintentar), la MISMA operación se encola con el MISMO
 *      `idOperacionCliente`: si llegó a aplicarse, la sincronización la reconoce (YA_APLICADA). Nunca dos consumos.
 *   4. Sin conexión, se encola.
 *
 * Un conflicto o rechazo del servidor en línea (parte anulado, reasignado, sin stock…) se devuelve tal cual: nunca se
 * encola algo que el servidor ya dijo que no.
 */
import { api, FalloApi } from '../api.ts';
import { leerMutacion, type ResultadoMntMutacion } from './db.ts';
import { esFalloSinRespuesta } from './ejecucion-propia.ts';
import { cabeceraPerfilOffline } from './identidad-sync.ts';
import { encolarFoto, encolarOperacion, operacionesDeParte, enCamino, type DatosFoto, type OperacionMnt } from './mantenimiento-local.ts';
import { sincronizarPendientes } from './sync.ts';

export interface ContextoMnt {
  /** Perfil offline DESBLOQUEADO de este usuario, o `null` si el dispositivo no está preparado. */
  readonly perfilId: string | null;
  /** Conexión real con el servidor (`pwa.ts::useConectividad`). */
  readonly enLinea: boolean;
}

export type ResultadoAccionMnt =
  | { readonly ok: true; readonly donde: 'SERVIDOR'; readonly aviso: string | null; readonly mnt: ResultadoMntMutacion | null }
  | { readonly ok: true; readonly donde: 'DISPOSITIVO'; readonly aviso: string | null }
  | { readonly ok: false; readonly motivo: string };

export const MOTIVO_SIN_PREPARAR_MNT =
  'Sin conexión. Para guardar trabajo sin conexión, prepara antes este dispositivo desde «Mis partes → Preparar sin conexión».';

/** Lo que dice el servidor al ajustar la hora (`campo.ts::AvisoHora`), en palabras. */
export function textoAvisosHora(avisos: readonly string[]): string | null {
  if (avisos.includes('RELOJ_DISPOSITIVO_DESFASADO')) return 'La hora de este dispositivo no era correcta: se ha guardado con la hora corregida del servidor.';
  if (avisos.includes('HORA_FUTURA_AJUSTADA')) return 'La hora indicada era futura: se ha guardado con la hora del servidor.';
  if (avisos.includes('HORA_AJUSTADA')) return 'La hora se ha ajustado para que no se solape con otra visita.';
  return null;
}

type RespuestaItem =
  | { resultado: 'APLICADA' | 'YA_APLICADA'; mnt: ResultadoMntMutacion }
  | { resultado: 'CONFLICTO'; motivo: string }
  | { resultado: 'RECHAZADA' | 'ERROR_REINTENTABLE'; mensaje: string }
  | { resultado: 'OMITIDA'; motivo: string };

async function tienePendientes(perfilId: string, idPt: string): Promise<boolean> {
  return (await operacionesDeParte(perfilId, idPt)).some(enCamino);
}

async function encolar(perfilId: string, op: OperacionMnt, aviso: string | null, idOperacionCliente?: string): Promise<ResultadoAccionMnt> {
  if (idOperacionCliente !== undefined && (await leerMutacion(perfilId, idOperacionCliente)) !== null) {
    return { ok: true, donde: 'DISPOSITIVO', aviso };
  }
  const r = await encolarOperacion(perfilId, op, idOperacionCliente === undefined ? {} : { idOperacionCliente });
  if (!r.ok) return r;
  if (typeof navigator !== 'undefined' && navigator.onLine) void sincronizarPendientes(perfilId);
  return { ok: true, donde: 'DISPOSITIVO', aviso };
}

/** Registra una operación ya preparada (`mantenimiento-local.ts::preparar*`). `aviso`: el de la preparación. */
export async function ejecutarOperacionMnt(ctx: ContextoMnt, op: OperacionMnt, aviso: string | null): Promise<ResultadoAccionMnt> {
  const { perfilId } = ctx;
  if (perfilId !== null && (await tienePendientes(perfilId, op.idPt))) return encolar(perfilId, op, aviso);
  //  Una operación que depende de un inicio que aún no está en el servidor sólo puede ir detrás de él, en la cola.
  if (op.dependeDe !== null) {
    if (perfilId === null) return { ok: false, motivo: MOTIVO_SIN_PREPARAR_MNT };
    return encolar(perfilId, op, aviso);
  }

  if (ctx.enLinea) {
    const idOperacionCliente = crypto.randomUUID();
    let item: RespuestaItem | undefined;
    try {
      const r = await api.post<{ resultados: RespuestaItem[] }>(
        '/api/sync',
        {
          relojDispositivoEn: new Date().toISOString(),
          mutaciones: [{
            idOperacionCliente, tipo: op.tipo, idEntidad: op.idEntidad, idOt: op.idPt, revisionBase: op.revisionBase,
            fechaEfectiva: op.fechaEfectiva, payload: op.payload, dependeDe: null,
          }],
        },
        perfilId === null ? {} : { headers: cabeceraPerfilOffline(perfilId) },
      );
      item = r.resultados[0];
    } catch (e) {
      if (!esFalloSinRespuesta(e)) {
        return { ok: false, motivo: e instanceof FalloApi || e instanceof Error ? e.message : 'No se ha podido guardar.' };
      }
      if (perfilId === null) return { ok: false, motivo: MOTIVO_SIN_PREPARAR_MNT };
      return encolar(perfilId, op, aviso, idOperacionCliente);
    }
    if (item === undefined) {
      if (perfilId === null) return { ok: false, motivo: 'El servidor no ha respondido como se esperaba. Inténtalo de nuevo.' };
      return encolar(perfilId, op, aviso, idOperacionCliente);
    }
    switch (item.resultado) {
      case 'APLICADA':
      case 'YA_APLICADA': {
        if (perfilId !== null) {
          //  Mejor esfuerzo: si el dispositivo no tiene paquete todavía, no hay nada que anotar.
          await encolarOperacion(perfilId, op, { idOperacionCliente, aplicadaEnServidor: item.mnt }).catch(() => undefined);
        }
        const hora = textoAvisosHora(item.mnt.avisos);
        return { ok: true, donde: 'SERVIDOR', aviso: [aviso, hora].filter((x): x is string => x !== null).join(' ') || null, mnt: item.mnt };
      }
      case 'ERROR_REINTENTABLE':
        if (perfilId === null) return { ok: false, motivo: item.mensaje };
        return encolar(perfilId, op, aviso, idOperacionCliente);
      case 'CONFLICTO':
      case 'OMITIDA':
        return { ok: false, motivo: item.motivo };
      case 'RECHAZADA':
        return { ok: false, motivo: item.mensaje };
    }
  }

  if (perfilId === null) return { ok: false, motivo: MOTIVO_SIN_PREPARAR_MNT };
  return encolar(perfilId, op, aviso);
}

/**
 * Foto del parte: en línea por la ruta de siempre (`/api/mantenimiento/partes/:id/evidencias`, multipart e
 * idempotente); sin respuesta o sin conexión, al almacén de evidencias del dispositivo con el MISMO id.
 */
export async function subirFotoParte(ctx: ContextoMnt, f: DatosFoto, aviso: string | null): Promise<ResultadoAccionMnt> {
  const idOperacionCliente = crypto.randomUUID();
  const pendientes = ctx.perfilId !== null && (await tienePendientes(ctx.perfilId, f.idPt));
  if (ctx.enLinea && !pendientes && f.dependeDe === null) {
    try {
      const datos = new FormData();
      datos.set('contexto', f.contexto);
      datos.set('idOperacionCliente', idOperacionCliente);
      datos.set('archivo', f.archivo);
      const consulta = f.idIntervencion === null ? '' : `?idIntervencion=${f.idIntervencion}`;
      await api.subir(`/api/mantenimiento/partes/${f.idPt}/evidencias${consulta}`, datos);
      return { ok: true, donde: 'SERVIDOR', aviso, mnt: null };
    } catch (e) {
      if (!esFalloSinRespuesta(e)) return { ok: false, motivo: e instanceof Error ? e.message : 'No se ha podido subir la foto.' };
    }
  }
  if (ctx.perfilId === null) return { ok: false, motivo: MOTIVO_SIN_PREPARAR_MNT };
  const r = await encolarFoto(ctx.perfilId, { ...f, idOperacionCliente });
  if (!r.ok) return r;
  if (typeof navigator !== 'undefined' && navigator.onLine) void sincronizarPendientes(ctx.perfilId);
  return { ok: true, donde: 'DISPOSITIVO', aviso };
}
